import { normalizeTransportAddressList } from '../../utils/transportAddressUtils';
export { normalizeTransportAddressList } from '../../utils/transportAddressUtils';

export function buildTransportAddressFields(source: Record<string, unknown>): {
  loadingAddresses: string[];
  deliveryAddresses: string[];
} {
  const loadingAddresses = normalizeTransportAddressList(source.loadingAddresses);
  const deliveryAddresses = normalizeTransportAddressList(source.deliveryAddresses);

  return {
    loadingAddresses,
    deliveryAddresses,
  };
}

function stripTechnicalRoutePrefix(value: string): string {
  return value
    .replace(/^[A-Z]{2}-[A-Z0-9]+-\d{12,}\s+/i, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function compactEndpointLabel(value: string): string {
  return stripTechnicalRoutePrefix(value)
    .replace(/\bLoading Dock\b/i, '')
    .replace(/\bDelivery Gate\b/i, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function isLowSignalEndpointLabel(value: string): boolean {
  return /^(UI\s+)?Loading\b/i.test(value) || /^(UI\s+)?Delivery\b/i.test(value);
}

export function extractTransportRouteEndpoint(address: string | undefined): string {
  if (!address) return '';
  const lines = address
    .split(/\n|;/)
    .map((line) => line.trim())
    .filter(Boolean);

  for (const line of lines) {
    const postalMatch = line.match(/\b\d{2}-\d{3}\s+(.+?)(?:,|$)/);
    if (postalMatch) return postalMatch[1].trim();
  }

  const addressParts = address
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  const countryNames = new Set(['polska', 'poland', 'pl']);
  const candidateParts = addressParts.filter((part) => !countryNames.has(part.toLowerCase()));
  const cityCandidate = candidateParts[candidateParts.length - 1];

  if (cityCandidate && !/^\d/.test(cityCandidate) && cityCandidate.length < 48) {
    return compactEndpointLabel(cityCandidate);
  }

  const firstLine = compactEndpointLabel(lines[0] || '');
  return firstLine.slice(0, 40);
}

function resolveRouteEndpoints(addresses: string[], fallback?: string | null): string[] {
  const seen = new Set<string>();
  const endpoints: string[] = [];
  addresses.forEach((address) => {
    const endpoint = extractTransportRouteEndpoint(address);
    const key = endpoint.toLocaleLowerCase('pl-PL');
    if (!endpoint || isLowSignalEndpointLabel(endpoint) || seen.has(key)) return;
    seen.add(key);
    endpoints.push(endpoint);
  });
  if (endpoints.length > 0) return endpoints;

  const fallbackEndpoint = extractTransportRouteEndpoint(fallback || undefined);
  if (fallbackEndpoint) return [fallbackEndpoint];
  const firstAddressEndpoint = extractTransportRouteEndpoint(addresses[0]);
  return firstAddressEndpoint ? [firstAddressEndpoint] : [];
}

/**
 * Loading and delivery addresses are independent lists (unequal counts are
 * valid), so the route is "all loadings → all deliveries" — never a pairing.
 * `origins` / `destinations` hold every distinct stop in address order;
 * `origin` / `destination` stay the first stop for callers that need one.
 */
export function buildTransportRouteLabel(
  loadingAddresses: string[],
  deliveryAddresses: string[],
  fallbackOrigin?: string | null,
  fallbackDestination?: string | null,
): {
  label: string;
  origin: string;
  destination: string;
  origins: string[];
  destinations: string[];
  tooltip: string;
} {
  const origins = resolveRouteEndpoints(loadingAddresses, fallbackOrigin);
  const destinations = resolveRouteEndpoints(deliveryAddresses, fallbackDestination);

  if (origins.length === 0 && destinations.length === 0) {
    return { label: '-', origin: '-', destination: '-', origins: [], destinations: [], tooltip: '-' };
  }

  const resolvedOrigins = origins.length > 0 ? origins : ['?'];
  const resolvedDestinations = destinations.length > 0 ? destinations : ['?'];
  const label = `${resolvedOrigins.join(' + ')} → ${resolvedDestinations.join(' + ')}`;
  const tooltipParts: string[] = [];
  if (loadingAddresses.length > 0) tooltipParts.push(loadingAddresses.join('\n\n'));
  if (deliveryAddresses.length > 0) tooltipParts.push(deliveryAddresses.join('\n\n'));

  return {
    label,
    origin: resolvedOrigins[0],
    destination: resolvedDestinations[0],
    origins: resolvedOrigins,
    destinations: resolvedDestinations,
    tooltip: tooltipParts.join('\n↓\n') || label,
  };
}
